import './utils/wdyr'

// Configure regeneratorRuntime first, so we don't error out
import './runtimeConfig'
import './rn-compatibility'
import 'core-js/stable'
import 'pwacompat'

import React from 'react'
import { createRoot } from 'react-dom/client'
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'

import App from './components/App'

const POSTHOG_KEY = process.env.REACT_APP_PUBLIC_POSTHOG_KEY
const POSTHOG_HOST =
  process.env.REACT_APP_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com'

/**
 * Initialize PostHog and resolve once the first batch of feature flags is
 * available. Mirrors the deferred-render behavior we had with
 * `asyncWithLDProvider`, so initial render isn't gated on `flags` being empty.
 */
const initializePostHog = () =>
  new Promise(resolve => {
    if (!POSTHOG_KEY) {
      // No key configured (e.g. local dev): proceed with empty flags rather
      // than blocking the app from rendering.
      // eslint-disable-next-line no-console
      console.warn(
        'REACT_APP_PUBLIC_POSTHOG_KEY is not set — feature flags will be empty.'
      )
      resolve()

      return
    }

    let settled = false
    const finish = () => {
      if (settled) return
      settled = true
      resolve()
    }

    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      // Pre-init distinct id mirrors the old LD `pre-init-fixed-key`.
      bootstrap: { distinctID: 'pre-init-fixed-key' },
      persistence: 'localStorage+cookie',
      capture_pageview: false,
      loaded: () => {
        // Resolve as soon as flags are available; fall back to the loaded
        // callback so we never hang forever if PostHog can't reach the server.
        posthog.onFeatureFlags(finish)
      },
    })

    // Safety net: don't block initial render for more than 2s waiting on flags.
    setTimeout(finish, 2000)
  })

async function main() {
  await initializePostHog()

  const container = document.getElementById('root')
  const root = createRoot(container)

  root.render(
    <PostHogProvider client={posthog}>
      <App />
    </PostHogProvider>
  )
}

main()
